import { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  Plus,
  Trash2,
  Building2,
  Award,
  Globe,
  Mail,
  ArrowRight,
  Save,
  Check,
  DollarSign,
  Users,
  Crown,
  FileText,
  Edit3,
  X,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { useProfileStore } from '@/lib/state/profile-store';
import { InvestmentRecord } from '@/lib/state/inverio-store';
import Badge, { calculateSeniorityLevel } from '@/components/Badge';
import { OnboardingLayout, INVESTOR_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import { useAlert } from '@/components/AlertDialog';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAuth, isSupabaseConfigured } from '@/lib/supabase';
import { upsertTrackRecords } from '@/lib/supabase/track-record';
import { handleError } from '@/lib/errors';

type InvestmentStage = 'pre-seed' | 'seed' | 'bridge' | 'series-a' | 'series-b' | 'series-c+';
type InvestmentType = 'direct' | 'syndicate';
type InvestorRole = 'none' | 'board-member' | 'board-observer' | 'advisor';
type SecurityType = 'safe' | 'convertible-note' | 'equity' | 'other';

interface PortfolioEntry {
  id: string;
  companyName: string;
  stage: InvestmentStage;
  investmentType: InvestmentType;
  syndicateLeadName?: string;
  securityType: SecurityType;
  year: string;
  valuationAtInvestment?: number;
  investorRole: InvestorRole;
  website?: string;
  founderEmail?: string;
}

interface NewEntry {
  companyName: string;
  stage: InvestmentStage;
  investmentType: InvestmentType;
  syndicateLeadName: string;
  securityType: SecurityType;
  year: string;
  valuationAtInvestment: string;
  investorRole: InvestorRole;
  website: string;
  founderEmail: string;
}

const STAGES: { value: InvestmentStage; label: string }[] = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'series-c+', label: 'Series C+' },
];

const INVESTMENT_TYPES: { value: InvestmentType; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'syndicate', label: 'Syndicate' },
];

const SECURITY_TYPES: { value: SecurityType; label: string }[] = [
  { value: 'safe', label: 'SAFE' },
  { value: 'convertible-note', label: 'Convertible Note' },
  { value: 'equity', label: 'Equity' },
  { value: 'other', label: 'Other' },
];

const ADDITIONAL_ROLES: { value: InvestorRole; label: string; points: number }[] = [
  { value: 'none', label: 'None', points: 0 },
  { value: 'advisor', label: 'Advisor', points: 2 },
  { value: 'board-observer', label: 'Board Observer', points: 3 },
  { value: 'board-member', label: 'Board Member', points: 5 },
];

export default function InvestorPortfolioScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const { alert, error: showError } = useAlert();
  // The AUTH user id — `investor_track_record.investor_user_id` is
  // `auth.users(id)`, not `investor_profiles.id`. Writing the profile id here
  // is what made every portfolio write fail silently before 2026-07-30.
  const { user: authUser } = useAuth();
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const user = useProfileStore((s) => s.user);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const skipOnboardingStep = useProfileStore((s) => s.skipOnboardingStep);

  // Convert existing investment history to portfolio entries
  const existingEntries: PortfolioEntry[] = (investorProfile?.investmentHistory ?? []).map((inv) => ({
    id: inv.id,
    companyName: inv.companyName,
    stage: inv.stage as InvestmentStage,
    investmentType: inv.investmentType,
    syndicateLeadName: inv.syndicateLeadName ?? '',
    securityType: inv.securityType ?? 'safe',
    year: inv.date.substring(0, 4),
    valuationAtInvestment: inv.valuationAtInvestment,
    investorRole: inv.investorRole ?? 'none',
  }));

  const [entries, setEntries] = useState<PortfolioEntry[]>(existingEntries);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newEntry, setNewEntry] = useState<NewEntry>({
    companyName: '',
    stage: 'seed',
    investmentType: 'direct',
    syndicateLeadName: '',
    securityType: 'safe',
    year: new Date().getFullYear().toString(),
    valuationAtInvestment: '',
    investorRole: 'none',
    website: '',
    founderEmail: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => (currentYear - i).toString());

  // Calculate seniority preview with enhanced scoring
  const previewYears = entries.length > 0
    ? currentYear - Math.min(...entries.map((e) => parseInt(e.year, 10)))
    : 0;

  // Calculate board/advisory engagement score
  const engagementScore = entries.reduce((acc, entry) => {
    const rolePoints = ADDITIONAL_ROLES.find((r) => r.value === entry.investorRole)?.points ?? 1;
    return acc + rolePoints;
  }, 0);

  // Total portfolio value at investment (for display)
  const totalPortfolioValue = entries.reduce((acc, entry) => {
    return acc + (entry.valuationAtInvestment ?? 0);
  }, 0);

  // Board seats count
  const boardSeats = entries.filter((e) => e.investorRole === 'board-member').length;
  const advisoryRoles = entries.filter((e) => e.investorRole === 'advisor' || e.investorRole === 'board-observer').length;

  // Enhanced seniority calculation (engagement score boosts effective portfolio count)
  const effectivePortfolioCount = entries.length + Math.floor(engagementScore / 5);
  const previewSeniority = calculateSeniorityLevel(previewYears, effectivePortfolioCount);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canAdd =
    newEntry.companyName.trim() &&
    (!newEntry.founderEmail.trim() || isValidEmail(newEntry.founderEmail.trim()));

  // Format valuation for display
  const formatValuation = (value: number | undefined): string => {
    if (!value) return '';
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(1)}B`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  const resetForm = () => {
    setNewEntry({
      companyName: '',
      stage: 'seed',
      investmentType: 'direct',
      syndicateLeadName: '',
      securityType: 'safe',
      year: new Date().getFullYear().toString(),
      valuationAtInvestment: '',
      investorRole: 'none',
      website: '',
      founderEmail: '',
    });
    setEditingId(null);
  };

  const startEdit = (entry: PortfolioEntry) => {
    setEditingId(entry.id);
    setNewEntry({
      companyName: entry.companyName,
      stage: entry.stage,
      investmentType: entry.investmentType,
      syndicateLeadName: entry.syndicateLeadName ?? '',
      securityType: entry.securityType,
      year: entry.year,
      valuationAtInvestment: entry.valuationAtInvestment?.toString() ?? '',
      investorRole: entry.investorRole,
      website: entry.website ?? '',
      founderEmail: entry.founderEmail ?? '',
    });
  };

  const cancelEdit = () => {
    resetForm();
  };

  const addOrUpdateEntry = () => {
    if (!canAdd) return;

    const valuationNum = newEntry.valuationAtInvestment
      ? parseFloat(newEntry.valuationAtInvestment)
      : undefined;

    if (editingId) {
      // Update existing entry
      setEntries(entries.map((e) =>
        e.id === editingId
          ? {
              ...e,
              companyName: newEntry.companyName.trim(),
              stage: newEntry.stage,
              investmentType: newEntry.investmentType,
              // Cleared on a direct deal so switching type does not leave a
              // stale lead that the DB constraint would then accept.
              syndicateLeadName:
                newEntry.investmentType === 'syndicate'
                  ? newEntry.syndicateLeadName.trim() || undefined
                  : undefined,
              securityType: newEntry.securityType,
              year: newEntry.year,
              valuationAtInvestment: valuationNum,
              investorRole: newEntry.investorRole,
              website: newEntry.website.trim() || undefined,
              founderEmail: newEntry.founderEmail.trim() || undefined,
            }
          : e
      ));
    } else {
      // Add new entry
      const entry: PortfolioEntry = {
        id: Date.now().toString(),
        companyName: newEntry.companyName.trim(),
        stage: newEntry.stage,
        investmentType: newEntry.investmentType,
        syndicateLeadName:
          newEntry.investmentType === 'syndicate'
            ? newEntry.syndicateLeadName.trim() || undefined
            : undefined,
        securityType: newEntry.securityType,
        year: newEntry.year,
        valuationAtInvestment: valuationNum,
        investorRole: newEntry.investorRole,
        website: newEntry.website.trim() || undefined,
        founderEmail: newEntry.founderEmail.trim() || undefined,
      };
      setEntries([...entries, entry]);
    }

    // Reset form
    resetForm();
  };

  const removeEntry = (id: string) => {
    setEntries(entries.filter((e) => e.id !== id));
    // If we're editing this entry, cancel the edit
    if (editingId === id) {
      resetForm();
    }
  };

  const saveProgress = async () => {
    setIsSaving(true);

    // Calculate years investing
    let yearsInvesting = 0;
    if (entries.length > 0) {
      const entryYears = entries.map((e) => parseInt(e.year, 10));
      const earliestYear = Math.min(...entryYears);
      yearsInvesting = currentYear - earliestYear;
    }

    // Calculate seniority level
    const seniorityLevel = calculateSeniorityLevel(yearsInvesting, entries.length);

    // Convert to InvestmentRecord format
    const investmentHistory: InvestmentRecord[] = entries.map((e) => ({
      id: e.id,
      companyName: e.companyName,
      stage: e.stage,
      investmentType: e.investmentType,
      syndicateLeadName: e.syndicateLeadName || undefined,
      securityType: e.securityType,
      date: `${e.year}-01-01`,
      valuationAtInvestment: e.valuationAtInvestment,
      investorRole: e.investorRole,
      source: 'manual' as const,
    }));

    // Save to store
    if (investorProfile) {
      setInvestorProfile({
        ...investorProfile,
        investmentHistory,
        portfolioCount: entries.length,
        yearsInvesting,
        seniorityLevel,
      });
    }

    // Persist to the server.
    //
    // This used to be `await new Promise(r => setTimeout(r, 500))` — a
    // SIMULATED save delay, followed unconditionally by the "Saved" tick. The
    // screen has always claimed to save and never written anything: the
    // entries lived in the local store only, so they vanished on sign-out or
    // on any other device. See
    // supabase/migrations/20260731100000_investor_track_record.sql.
    //
    // Awaited, and the tick is in the success path only — the same correction
    // made to the Portfolio screen on 2026-07-30, where a fire-and-forget
    // write buzzed "saved" on every rejection.
    if (authUser?.id && isSupabaseConfigured()) {
      try {
        await upsertTrackRecords(authUser.id, investmentHistory);
      } catch (err) {
        const { message, isCancelled } = handleError(err, {
          context: 'InvestorPortfolioOnboarding.saveProgress',
        });
        setIsSaving(false);
        // The entries stay in the local store and the investor shell
        // reconciles them on next launch, so nothing is lost meanwhile.
        if (!isCancelled) showError('Could not save', message);
        return;
      }
    }

    setIsSaving(false);
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
  };

  const handleContinue = async () => {
    // Save first
    await saveProgress();

    // Track as skipped if no entries added
    if (entries.length === 0) {
      skipOnboardingStep('portfolio');
    }

    router.push('/(onboarding)/complete');
  };

  const content = (
    <>
      {/* Summary Header */}
      <Animated.View entering={FadeInUp.delay(100).duration(500)} className="mb-6">
        <View
          className="p-4 rounded-2xl"
          style={{
            backgroundColor: colors.primary[600],
          }}
        >
          <View className="flex-row items-center justify-between">
            <View>
              <Text className="text-white/70 text-sm">Portfolio Companies</Text>
              <Text className="text-white text-3xl font-bold">{entries.length}</Text>
            </View>
            {entries.length > 0 && (
              <View className="items-end">
                <Text className="text-white/70 text-xs mb-1">Your Badge</Text>
                <Badge type="investor-seniority" size="sm" seniorityLevel={previewSeniority} />
              </View>
            )}
          </View>
          {entries.length > 0 && (
            <View className="mt-3 pt-3 border-t border-white/20">
              <View className="flex-row flex-wrap gap-x-4 gap-y-1">
                <Text className="text-white/70 text-xs">
                  {previewYears} year{previewYears !== 1 ? 's' : ''} investing
                </Text>
                {boardSeats > 0 && (
                  <Text className="text-white/70 text-xs">
                    {boardSeats} board seat{boardSeats !== 1 ? 's' : ''}
                  </Text>
                )}
                {advisoryRoles > 0 && (
                  <Text className="text-white/70 text-xs">
                    {advisoryRoles} advisory role{advisoryRoles !== 1 ? 's' : ''}
                  </Text>
                )}
                {totalPortfolioValue > 0 && (
                  <Text className="text-white/70 text-xs">
                    {formatValuation(totalPortfolioValue)} deployed
                  </Text>
                )}
              </View>
            </View>
          )}
        </View>
      </Animated.View>

      {/* Existing Entries List */}
      {entries.length > 0 && (
        <Animated.View entering={FadeInUp.delay(150).duration(500)} className="mb-6">
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm font-medium mb-3"
          >
            Your Portfolio
          </Text>
          <View className="gap-2">
            {entries.map((entry, index) => (
              <Animated.View
                key={entry.id}
                entering={FadeIn.delay(index * 50).duration(300)}
              >
                <View
                  className="p-3 rounded-xl flex-row items-center"
                  style={{
                    backgroundColor: editingId === entry.id ? colors.primary[50] : colors.surface.primary,
                    borderWidth: editingId === entry.id ? 2 : 1,
                    borderColor: editingId === entry.id ? colors.primary[600] : colors.border.light,
                  }}
                >
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3"
                    style={{
                      backgroundColor:
                        entry.investorRole === 'board-member'
                          ? colors.amber.light
                          : entry.investorRole === 'board-observer' || entry.investorRole === 'advisor'
                          ? colors.primary[50]
                          : colors.surface.secondary,
                    }}
                  >
                    {entry.investorRole === 'board-member' ? (
                      <Crown size={18} color={colors.amber.main} />
                    ) : entry.investorRole === 'advisor' || entry.investorRole === 'board-observer' ? (
                      <Users size={18} color={colors.primary[600]} />
                    ) : (
                      <Building2 size={18} color={colors.text.tertiary} />
                    )}
                  </View>
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2">
                      <Text
                        style={{ color: colors.text.primary }}
                        className="font-semibold text-sm"
                      >
                        {entry.companyName}
                      </Text>
                      {entry.investorRole && entry.investorRole !== 'none' && (
                        <View
                          className="px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor:
                              entry.investorRole === 'board-member'
                                ? colors.amber.light
                                : colors.primary[50],
                          }}
                        >
                          <Text
                            style={{
                              color:
                                entry.investorRole === 'board-member'
                                  ? colors.amber.main
                                  : colors.primary[600],
                            }}
                            className="text-[10px] font-medium"
                          >
                            {ADDITIONAL_ROLES.find((r) => r.value === entry.investorRole)?.label}
                          </Text>
                        </View>
                      )}
                    </View>
                    <Text
                      style={{ color: colors.text.tertiary }}
                      className="text-xs"
                    >
                      {STAGES.find((s) => s.value === entry.stage)?.label} • {entry.year}
                      {entry.securityType && ` • ${SECURITY_TYPES.find((s) => s.value === entry.securityType)?.label}`}
                      {entry.valuationAtInvestment && ` • ${formatValuation(entry.valuationAtInvestment)}`}
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-1">
                    <Pressable
                      onPress={() => startEdit(entry)}
                      className="p-2"
                      hitSlop={8}
                      disabled={editingId === entry.id}
                    >
                      <Edit3 size={16} color={editingId === entry.id ? colors.text.tertiary : colors.primary[600]} />
                    </Pressable>
                    <Pressable
                      onPress={() => removeEntry(entry.id)}
                      className="p-2"
                      hitSlop={8}
                    >
                      <Trash2 size={16} color={colors.traffic.red} />
                    </Pressable>
                  </View>
                </View>
              </Animated.View>
            ))}
          </View>
        </Animated.View>
      )}

      {/* Add New Entry Form - Always Visible */}
      <Animated.View entering={FadeInUp.delay(200).duration(500)} className="mb-6">
        <View
          className="p-4 rounded-xl"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.primary[200],
          }}
        >
          <View className="flex-row items-center justify-between mb-4">
            <View className="flex-row items-center gap-2">
              {editingId ? (
                <Edit3 size={18} color={colors.primary[600]} />
              ) : (
                <Plus size={18} color={colors.primary[600]} />
              )}
              <Text style={{ color: colors.text.primary }} className="font-semibold">
                {editingId ? 'Edit Company' : 'Add Company'}
              </Text>
            </View>
            {editingId && (
              <AnimatedPressable
                onPress={cancelEdit}
                className="flex-row items-center gap-1 px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <X size={14} color={colors.text.secondary} />
                <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                  Cancel
                </Text>
              </AnimatedPressable>
            )}
          </View>

          {/* Company Name */}
          <View className="mb-3">
            <TextInput
              value={newEntry.companyName}
              onChangeText={(text) => setNewEntry({ ...newEntry, companyName: text })}
              placeholder="Company name *"
              placeholderTextColor={colors.text.tertiary}
              className="px-4 py-3 rounded-xl text-base"
              style={{
                backgroundColor: colors.surface.secondary,
                color: colors.text.primary,
              }}
            />
          </View>

          {/* Stage - Compact chips */}
          <View className="mb-3">
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
              <View className="flex-row gap-2">
                {STAGES.map((stage) => (
                  <AnimatedPressable
                    key={stage.value}
                    onPress={() => setNewEntry({ ...newEntry, stage: stage.value })}
                    className="px-3 py-2 rounded-full"
                    style={{
                      backgroundColor:
                        newEntry.stage === stage.value
                          ? colors.primary[600]
                          : colors.surface.secondary,
                    }}
                  >
                    <Text
                      style={{
                        color: newEntry.stage === stage.value ? 'white' : colors.text.secondary,
                      }}
                      className="text-xs font-medium"
                    >
                      {stage.label}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* Type + Year Row */}
          <View className="flex-row gap-2 mb-3">
            {/* Investment Type */}
            <View className="flex-1 flex-row gap-1">
              {INVESTMENT_TYPES.map((type) => (
                <AnimatedPressable
                  key={type.value}
                  onPress={() => setNewEntry({ ...newEntry, investmentType: type.value })}
                  className="flex-1 py-2 rounded-lg items-center"
                  style={{
                    backgroundColor:
                      newEntry.investmentType === type.value
                        ? colors.primary[600]
                        : colors.surface.secondary,
                  }}
                >
                  <Text
                    style={{
                      color: newEntry.investmentType === type.value ? 'white' : colors.text.secondary,
                    }}
                    className="text-xs font-medium"
                  >
                    {type.label}
                  </Text>
                </AnimatedPressable>
              ))}
            </View>

            {/*
              Syndicate lead — shown only for syndicate deals.

              A founder frequently has no idea who the individual syndicate
              participants are: the cheque, the negotiation and the cap-table
              line all sit with the LEAD. Asking them to confirm an SPV
              participant they never met gets a truthful "no idea", and an
              unrecognised claim reads as a false one — so a CORRECT claim fails
              verification. Naming the lead is what makes it recognisable.
              Required by the database for syndicate claims
              (pvr_syndicate_lead_required).
            */}
            {newEntry.investmentType === 'syndicate' && (
              <View className="mb-3">
                <Text style={{ color: colors.text.secondary }} className="text-xs mb-1">
                  Syndicate lead
                </Text>
                <TextInput
                  value={newEntry.syndicateLeadName}
                  onChangeText={(t) => setNewEntry({ ...newEntry, syndicateLeadName: t })}
                  placeholder="Who led the round?"
                  placeholderTextColor={colors.text.tertiary}
                  style={{
                    backgroundColor: colors.surface.secondary,
                    color: colors.text.primary,
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                  }}
                />
                <Text style={{ color: colors.text.tertiary }} className="text-[11px] mt-1">
                  The founder may only know the lead, not each participant.
                </Text>
              </View>
            )}

            {/* Year Selector */}
            <View className="flex-1">
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                <View className="flex-row gap-1">
                  {years.slice(0, 8).map((year) => (
                    <AnimatedPressable
                      key={year}
                      onPress={() => setNewEntry({ ...newEntry, year })}
                      className="px-3 py-2 rounded-lg"
                      style={{
                        backgroundColor:
                          newEntry.year === year
                            ? colors.primary[600]
                            : colors.surface.secondary,
                      }}
                    >
                      <Text
                        style={{
                          color: newEntry.year === year ? 'white' : colors.text.secondary,
                        }}
                        className="text-xs font-medium"
                      >
                        {year}
                      </Text>
                    </AnimatedPressable>
                  ))}
                </View>
              </ScrollView>
            </View>
          </View>

          {/* Valuation at Investment */}
          <View className="mb-3">
            <View className="flex-row items-center gap-2 mb-2">
              <DollarSign size={14} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                Valuation at Investment (USD)
              </Text>
            </View>
            <View
              className="flex-row items-center rounded-xl overflow-hidden"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <View className="px-3 py-3">
                <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">$</Text>
              </View>
              <TextInput
                value={newEntry.valuationAtInvestment}
                onChangeText={(text) => {
                  // Only allow numbers and decimal point
                  const cleaned = text.replace(/[^0-9.]/g, '');
                  setNewEntry({ ...newEntry, valuationAtInvestment: cleaned });
                }}
                placeholder="e.g. 5000000"
                placeholderTextColor={colors.text.tertiary}
                keyboardType="numeric"
                className="flex-1 py-3 pr-3 text-sm"
                style={{ color: colors.text.primary }}
              />
              <View className="pr-3">
                <Text style={{ color: colors.text.tertiary }} className="text-xs">Optional</Text>
              </View>
            </View>
          </View>

          {/* Security Type */}
          <View className="mb-3">
            <View className="flex-row items-center gap-2 mb-2">
              <FileText size={14} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                Security Type
              </Text>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
              <View className="flex-row gap-2">
                {SECURITY_TYPES.map((security) => (
                  <AnimatedPressable
                    key={security.value}
                    onPress={() => setNewEntry({ ...newEntry, securityType: security.value })}
                    className="px-3 py-2 rounded-lg"
                    style={{
                      backgroundColor:
                        newEntry.securityType === security.value
                          ? colors.primary[600]
                          : colors.surface.secondary,
                    }}
                  >
                    <Text
                      style={{
                        color: newEntry.securityType === security.value ? 'white' : colors.text.secondary,
                      }}
                      className="text-xs font-medium"
                    >
                      {security.label}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* Additional Role */}
          <View className="mb-3">
            <View className="flex-row items-center gap-2 mb-2">
              <Crown size={14} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                Additional Role
              </Text>
            </View>
            <View className="flex-row flex-wrap gap-2">
              {ADDITIONAL_ROLES.map((role) => (
                <AnimatedPressable
                  key={role.value}
                  onPress={() => setNewEntry({ ...newEntry, investorRole: role.value })}
                  className="px-3 py-2 rounded-lg flex-row items-center gap-1"
                  style={{
                    backgroundColor:
                      newEntry.investorRole === role.value
                        ? role.value === 'board-member'
                          ? colors.amber.main
                          : colors.primary[600]
                        : colors.surface.secondary,
                  }}
                >
                  {role.value === 'board-member' && (
                    <Crown size={12} color={newEntry.investorRole === role.value ? 'white' : colors.amber.main} />
                  )}
                  <Text
                    style={{
                      color: newEntry.investorRole === role.value ? 'white' : colors.text.secondary,
                    }}
                    className="text-xs font-medium"
                  >
                    {role.label}
                  </Text>
                </AnimatedPressable>
              ))}
            </View>
          </View>

          {/* Optional Fields - Collapsed by default */}
          <View className="flex-row gap-2 mb-4">
            <View className="flex-1">
              <View className="flex-row items-center rounded-xl overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                <View className="px-3 py-3">
                  <Globe size={16} color={colors.text.tertiary} />
                </View>
                <TextInput
                  value={newEntry.website}
                  onChangeText={(text) => setNewEntry({ ...newEntry, website: text })}
                  placeholder="Website"
                  placeholderTextColor={colors.text.tertiary}
                  autoCapitalize="none"
                  keyboardType="url"
                  className="flex-1 py-3 pr-3 text-sm"
                  style={{ color: colors.text.primary }}
                />
              </View>
            </View>
            <View className="flex-1">
              <View className="flex-row items-center rounded-xl overflow-hidden" style={{ backgroundColor: colors.surface.secondary }}>
                <View className="px-3 py-3">
                  <Mail size={16} color={colors.text.tertiary} />
                </View>
                <TextInput
                  value={newEntry.founderEmail}
                  onChangeText={(text) => setNewEntry({ ...newEntry, founderEmail: text })}
                  placeholder="Founder email"
                  placeholderTextColor={colors.text.tertiary}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  className="flex-1 py-3 pr-3 text-sm"
                  style={{ color: colors.text.primary }}
                />
              </View>
            </View>
          </View>

          {/* Add/Update Button */}
          <AnimatedPressable
            onPress={addOrUpdateEntry}
            disabled={!canAdd}
            className="py-3 rounded-xl items-center flex-row justify-center gap-2"
            style={{
              backgroundColor: canAdd ? colors.primary[600] : colors.border.light,
            }}
          >
            {editingId ? (
              <Check size={18} color={canAdd ? 'white' : colors.text.tertiary} />
            ) : (
              <Plus size={18} color={canAdd ? 'white' : colors.text.tertiary} />
            )}
            <Text
              style={{ color: canAdd ? 'white' : colors.text.tertiary }}
              className="font-semibold"
            >
              {editingId ? 'Update Entry' : 'Add to Portfolio'}
            </Text>
          </AnimatedPressable>
        </View>
      </Animated.View>

      {/* Action Buttons */}
      <Animated.View entering={FadeInUp.delay(300).duration(500)} className="gap-3">
        {/* Save Progress Button - Only show if there are entries */}
        {entries.length > 0 && (
          <AnimatedPressable
            onPress={saveProgress}
            disabled={isSaving}
            className="py-3 rounded-xl flex-row items-center justify-center gap-2"
            style={{
              backgroundColor: justSaved ? colors.traffic.greenLight : colors.surface.primary,
              borderWidth: 1,
              borderColor: justSaved ? colors.traffic.green : colors.border.light,
            }}
          >
            {justSaved ? (
              <>
                <Check size={18} color={colors.traffic.green} />
                <Text style={{ color: colors.traffic.green }} className="font-medium">
                  Progress Saved!
                </Text>
              </>
            ) : (
              <>
                <Save size={18} color={colors.primary[600]} />
                <Text style={{ color: colors.primary[600] }} className="font-medium">
                  {isSaving ? 'Saving...' : 'Save Progress'}
                </Text>
              </>
            )}
          </AnimatedPressable>
        )}

        {/* Continue Button */}
        <AnimatedPressable
          onPress={handleContinue}
          className="py-4 rounded-xl flex-row items-center justify-center gap-2"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
            {entries.length > 0 ? 'Continue' : 'Skip for now'}
          </Text>
          <ArrowRight size={20} color={colors.text.inverse} />
        </AnimatedPressable>

        {entries.length === 0 && (
          <Text style={{ color: colors.text.tertiary }} className="text-xs text-center">
            We'll remind you to add your portfolio later
          </Text>
        )}
      </Animated.View>
    </>
  );

  // Desktop: Use OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="portfolio"
        steps={INVESTOR_ONBOARDING_STEPS}
        title="Your Portfolio"
        subtitle="Add all the companies you've invested in"
        userType="investor"
        entityName={user?.name}
      >
        {content}
      </OnboardingLayout>
    );
  }

  // Mobile: Full-screen with OnboardingLayout wrapper
  return (
    <OnboardingLayout
      currentStep="portfolio"
      steps={INVESTOR_ONBOARDING_STEPS}
      title="Your Portfolio"
      subtitle="Add all the companies you've invested in"
      userType="investor"
      entityName={user?.name}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        className="flex-1"
      >
        {content}
      </KeyboardAvoidingView>
    </OnboardingLayout>
  );
}
